import { toIntlLocale } from 'shared/helpers/intlLocale';

// SiteUP CRM (Módulo Imobiliária): ficha do lead para imprimir (roadmap
// ficha-lead, F1 a F5; mockup design-panels/ficha-lead). Aqui mora o que não
// depende de tela: a ordem dos campos da ficha, a formatação do que vem da API e
// o modelo editável. Os imóveis não entram no modelo editável: vêm do inventário
// e a ficha só os mostra.

// Ordem e largura dos campos, como no modelo em papel da imobiliária.
export const LEAD_SHEET_SECTIONS = [
  {
    key: 'cliente',
    label: 'CLIENT',
    fields: [
      { key: 'nome', label: 'NOME' },
      { key: 'telefone', label: 'TELEFONE' },
      { key: 'origem', label: 'ORIGEM' },
      { key: 'email', label: 'EMAIL' },
      { key: 'objetivo', label: 'OBJETIVO', full: true },
    ],
  },
  {
    key: 'busca',
    label: 'SEARCH',
    fields: [
      { key: 'investimento', label: 'INVESTIMENTO' },
      { key: 'perfil_bairro', label: 'PERFIL_BAIRRO' },
      { key: 'bairros', label: 'BAIRROS', full: true },
      { key: 'pagamento', label: 'PAGAMENTO' },
    ],
  },
  {
    key: 'requisitos',
    label: 'REQUIREMENTS',
    fields: [
      { key: 'estrutura', label: 'ESTRUTURA' },
      { key: 'dormitorios', label: 'DORMITORIOS' },
      { key: 'garagem', label: 'GARAGEM' },
      { key: 'area_externa', label: 'AREA_EXTERNA' },
    ],
  },
];

export const LEAD_SHEET_SERVICE_SECTION = {
  key: 'atendimento',
  label: 'SERVICE',
  fields: [
    { key: 'visita', label: 'VISITA' },
    { key: 'local', label: 'LOCAL' },
    { key: 'corretor', label: 'CORRETOR' },
    { key: 'etapa', label: 'ETAPA' },
  ],
};

export const PROPERTY_STATUSES = [
  'sent',
  'visited',
  'visit_scheduled',
  'liked',
  'disliked',
];

const text = value =>
  value === null || value === undefined ? '' : String(value).trim();

const intlLocale = locale => toIntlLocale(locale || 'pt-BR');

// Telefone brasileiro em E.164 vira o formato que se escreve à mão. Qualquer
// outro formato fica como veio do contato.
export function formatPhone(raw) {
  const value = text(raw);
  const digits = value.replace(/\D/g, '');
  if (!value.startsWith('+55') || ![12, 13].includes(digits.length)) {
    return value;
  }
  const area = digits.slice(2, 4);
  const number = digits.slice(4);
  const split = number.length - 4;
  return `(${area}) ${number.slice(0, split)}-${number.slice(split)}`;
}

export function formatCurrency(value, locale = 'pt-BR') {
  const number = Number(value);
  if (value === null || value === undefined || !Number.isFinite(number)) {
    return '';
  }
  return new Intl.NumberFormat(intlLocale(locale), {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  }).format(number);
}

export function formatArea(value, locale = 'pt-BR') {
  const number = Number(value);
  if (value === null || value === undefined || !Number.isFinite(number)) {
    return '';
  }
  const formatted = new Intl.NumberFormat(intlLocale(locale), {
    maximumFractionDigits: 0,
  }).format(number);
  return `${formatted} m²`;
}

const pad = number => String(number).padStart(2, '0');

// "Sáb 27/09 às 10h30" em pt_BR. O texto de ligação ("às", "at") vem do i18n.
export function formatVisit(iso, { locale = 'pt-BR', t = key => key } = {}) {
  const date = iso ? new Date(iso) : null;
  if (!date || Number.isNaN(date.getTime())) return '';
  const weekday = new Intl.DateTimeFormat(intlLocale(locale), {
    weekday: 'short',
  })
    .format(date)
    .replace('.', '');
  const capitalized = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  const isPt = intlLocale(locale).startsWith('pt');
  const day = isPt
    ? `${pad(date.getDate())}/${pad(date.getMonth() + 1)}`
    : `${pad(date.getMonth() + 1)}/${pad(date.getDate())}`;
  const time = isPt
    ? `${pad(date.getHours())}h${pad(date.getMinutes())}`
    : `${pad(date.getHours())}:${pad(date.getMinutes())}`;
  return t('CONVERSATION.LEAD_SHEET.VISIT_FORMAT', {
    weekday: capitalized,
    date: day,
    time,
  });
}

export function formatGeneratedAt(iso, locale = 'pt-BR') {
  const date = iso ? new Date(iso) : new Date();
  const safe = Number.isNaN(date.getTime()) ? new Date() : date;
  return {
    date: new Intl.DateTimeFormat(intlLocale(locale), {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(safe),
    time: `${pad(safe.getHours())}:${pad(safe.getMinutes())}`,
  };
}

const pickFields = (source, fields) =>
  Object.fromEntries(
    fields.map(field => [field.key, text(source?.[field.key])])
  );

// Resposta da API vira o modelo que a tela edita: tudo texto, nada nulo. O que
// a ficha não achou fica '' e aparece como "não informado".
export function toEditableSheet(payload = {}, { locale, t } = {}) {
  const sections = Object.fromEntries(
    LEAD_SHEET_SECTIONS.map(section => [
      section.key,
      pickFields(payload[section.key], section.fields),
    ])
  );
  sections.cliente.telefone = formatPhone(sections.cliente.telefone);

  const atendimento = pickFields(
    payload.atendimento,
    LEAD_SHEET_SERVICE_SECTION.fields
  );
  const visitaEm = payload.atendimento?.visita_em;
  if (visitaEm) atendimento.visita = formatVisit(visitaEm, { locale, t });

  const imoveis = (payload.imoveis || []).map(row => ({
    codigo: text(row.codigo),
    bairro: text(row.bairro),
    valor: formatCurrency(row.valor, locale),
    dormitorios: text(row.dormitorios),
    vagas: text(row.vagas),
    area: formatArea(row.area, locale),
    status: PROPERTY_STATUSES.includes(row.status) ? row.status : '',
  }));

  return {
    ...sections,
    atendimento,
    imoveis,
    observacoes: text(payload.observacoes),
    org: {
      nome: text(payload.org?.nome),
      detalhe: text(payload.org?.detalhe),
    },
    conversationId: payload.conversation_id ?? '',
    generatedAt: payload.generated_at || '',
    generatedBy: text(payload.generated_by),
    partial: Boolean(payload.partial),
  };
}
